import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  Shield, 
  Users, 
  ArrowUpCircle,
  X,
  ChevronLeft,
  ChevronRight,
  Clock,
  AlertTriangle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import api from '@/services/api';
import { useToast } from '@/hooks/useToast';

// Types
interface SLAPolicy {
  id: string;
  name: string;
  description?: string | null;
  priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT' | 'CRITICAL';
  firstResponseMinutes: number;
  resolutionMinutes: number;
  businessHoursOnly?: boolean;
  businessStartHour?: number;
  businessEndHour?: number;
  workingDays?: string;
  escalationEnabled?: boolean;
  isActive: boolean;
  isDefault?: boolean;
  createdAt?: string;
}

interface SupportTeam {
  id: string;
  name: string;
  description?: string;
  email?: string;
  isActive: boolean;
  members: TeamMember[];
  createdAt: string;
}

interface TeamMember {
  id: string;
  userId: string;
  role: 'LEAD' | 'MEMBER';
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
}

interface EscalationRule {
  id: string;
  name: string;
  description?: string;
  triggerAfterHours: number;
  escalateToTeamId: string;
  escalateToTeam?: SupportTeam;
  priority?: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT' | 'CRITICAL';
  isActive: boolean;
  createdAt: string;
}

type TabType = 'policies' | 'teams' | 'escalation';

// Initial form data
const initialPolicyForm = {
  name: '',
  description: '',
  firstResponseMinutes: 240,
  resolutionMinutes: 1440,
  priority: 'MEDIUM' as 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT' | 'CRITICAL',
  businessHoursOnly: true,
  isActive: true,
};

const initialTeamForm = {
  name: '',
  description: '',
  email: '',
  isActive: true,
};

const initialEscalationForm = {
  name: '',
  description: '',
  triggerAfterHours: 2,
  escalateToTeamId: '',
  priority: '' as string,
  isActive: true,
};

export default function SLAPage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [activeTab, setActiveTab] = useState<TabType>('policies');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  
  // Modal states
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [showEscalationModal, setShowEscalationModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  // Edit states
  const [editingPolicy, setEditingPolicy] = useState<SLAPolicy | null>(null);
  const [editingTeam, setEditingTeam] = useState<SupportTeam | null>(null);
  const [editingRule, setEditingRule] = useState<EscalationRule | null>(null);
  const [deletingItem, setDeletingItem] = useState<{ type: TabType; id: string } | null>(null);
  
  // Form states
  const [policyForm, setPolicyForm] = useState(initialPolicyForm);
  const [teamForm, setTeamForm] = useState(initialTeamForm);
  const [escalationForm, setEscalationForm] = useState(initialEscalationForm);

  // Fetch SLA Policies
  const { data: policiesData, isLoading: policiesLoading } = useQuery({
    queryKey: ['slaPolicies', { page, search }],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', '20');
      if (search) params.append('search', search);
      const response = await api.get(`/sla/policies?${params.toString()}`);
      return response.data;
    },
    enabled: activeTab === 'policies',
  });

  // Fetch Support Teams
  const { data: teamsData, isLoading: teamsLoading } = useQuery({
    queryKey: ['supportTeams', { page, search }],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', '20');
      if (search) params.append('search', search);
      const response = await api.get(`/sla/teams?${params.toString()}`);
      const responseData = response.data;
      // Handle both single object and array responses
      if (responseData && !responseData.data) {
        // If it's a direct array or single object (no pagination wrapper)
        if (Array.isArray(responseData)) {
          return { data: responseData, pagination: null };
        } else if (responseData.id) {
          // Single object response
          return { data: [responseData], pagination: null };
        }
      }
      return responseData;
    },
    enabled: activeTab === 'teams',
  });

  // Fetch Escalation Rules
  const { data: escalationData, isLoading: escalationLoading } = useQuery({
    queryKey: ['escalationRules', { page, search }],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', '20');
      if (search) params.append('search', search);
      const response = await api.get(`/sla/escalation-rules?${params.toString()}`);
      return response.data;
    },
    enabled: activeTab === 'escalation',
  });

  // Fetch all teams for escalation dropdown
  const { data: allTeamsData } = useQuery({
    queryKey: ['allSupportTeams'],
    queryFn: async () => {
      const response = await api.get('/sla/teams?limit=100');
      const responseData = response.data;
      // Handle both single object and array responses
      if (responseData && !responseData.data) {
        if (Array.isArray(responseData)) {
          return { data: responseData };
        } else if (responseData.id) {
          return { data: [responseData] };
        }
      }
      return responseData;
    },
    enabled: activeTab === 'escalation' && showEscalationModal,
  });

  // CRUD Mutations for Policies
  const createPolicyMutation = useMutation({
    mutationFn: async (data: typeof policyForm) => api.post('/sla/policies', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['slaPolicies'] });
      closePolicyModal();
      toast({ title: 'Success', description: 'SLA policy created successfully' });
    },
    onError: () => toast({ title: 'Error', description: 'Failed to create policy', variant: 'destructive' }),
  });

  const updatePolicyMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: typeof policyForm }) => api.put(`/sla/policies/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['slaPolicies'] });
      closePolicyModal();
      toast({ title: 'Success', description: 'SLA policy updated successfully' });
    },
    onError: () => toast({ title: 'Error', description: 'Failed to update policy', variant: 'destructive' }),
  });

  const deletePolicyMutation = useMutation({
    mutationFn: async (id: string) => api.delete(`/sla/policies/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['slaPolicies'] });
      closeDeleteConfirm();
      toast({ title: 'Success', description: 'SLA policy deleted successfully' });
    },
    onError: () => toast({ title: 'Error', description: 'Failed to delete policy', variant: 'destructive' }),
  });

  // CRUD Mutations for Teams
  const createTeamMutation = useMutation({
    mutationFn: async (data: typeof teamForm) => api.post('/sla/teams', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supportTeams'] });
      closeTeamModal();
      toast({ title: 'Success', description: 'Support team created successfully' });
    },
    onError: () => toast({ title: 'Error', description: 'Failed to create team', variant: 'destructive' }),
  });

  const updateTeamMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: typeof teamForm }) => api.put(`/sla/teams/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supportTeams'] });
      closeTeamModal();
      toast({ title: 'Success', description: 'Support team updated successfully' });
    },
    onError: () => toast({ title: 'Error', description: 'Failed to update team', variant: 'destructive' }),
  });

  const deleteTeamMutation = useMutation({
    mutationFn: async (id: string) => api.delete(`/sla/teams/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supportTeams'] });
      closeDeleteConfirm();
      toast({ title: 'Success', description: 'Support team deleted successfully' });
    },
    onError: () => toast({ title: 'Error', description: 'Failed to delete team', variant: 'destructive' }),
  });

  // CRUD Mutations for Escalation Rules
  const createEscalationMutation = useMutation({
    mutationFn: async (data: typeof escalationForm) => api.post('/sla/escalation-rules', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['escalationRules'] });
      closeEscalationModal();
      toast({ title: 'Success', description: 'Escalation rule created successfully' });
    },
    onError: () => toast({ title: 'Error', description: 'Failed to create rule', variant: 'destructive' }),
  });

  const updateEscalationMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: typeof escalationForm }) => api.put(`/sla/escalation-rules/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['escalationRules'] });
      closeEscalationModal();
      toast({ title: 'Success', description: 'Escalation rule updated successfully' });
    },
    onError: () => toast({ title: 'Error', description: 'Failed to update rule', variant: 'destructive' }),
  });

  const deleteEscalationMutation = useMutation({
    mutationFn: async (id: string) => api.delete(`/sla/escalation-rules/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['escalationRules'] });
      closeDeleteConfirm();
      toast({ title: 'Success', description: 'Escalation rule deleted successfully' });
    },
    onError: () => toast({ title: 'Error', description: 'Failed to delete rule', variant: 'destructive' }),
  });

  // Modal handlers
  const openCreatePolicyModal = () => {
    setEditingPolicy(null);
    setPolicyForm(initialPolicyForm);
    setShowPolicyModal(true);
  };

  const openEditPolicyModal = (policy: SLAPolicy) => {
    setEditingPolicy(policy);
    setPolicyForm({
      name: policy.name,
      description: policy.description || '',
      firstResponseMinutes: policy.firstResponseMinutes,
      resolutionMinutes: policy.resolutionMinutes,
      priority: policy.priority,
      businessHoursOnly: policy.businessHoursOnly ?? true,
      isActive: policy.isActive,
    });
    setShowPolicyModal(true);
  };

  const closePolicyModal = () => {
    setShowPolicyModal(false);
    setEditingPolicy(null);
    setPolicyForm(initialPolicyForm);
  };

  const openCreateTeamModal = () => {
    setEditingTeam(null);
    setTeamForm(initialTeamForm);
    setShowTeamModal(true);
  };

  const openEditTeamModal = (team: SupportTeam) => {
    setEditingTeam(team);
    setTeamForm({
      name: team.name,
      description: team.description || '',
      email: team.email || '',
      isActive: team.isActive,
    });
    setShowTeamModal(true);
  };

  const closeTeamModal = () => {
    setShowTeamModal(false);
    setEditingTeam(null);
    setTeamForm(initialTeamForm);
  };

  const openCreateEscalationModal = () => {
    setEditingRule(null);
    setEscalationForm(initialEscalationForm);
    setShowEscalationModal(true);
  };

  const openEditEscalationModal = (rule: EscalationRule) => {
    setEditingRule(rule);
    setEscalationForm({
      name: rule.name,
      description: rule.description || '',
      triggerAfterHours: rule.triggerAfterHours,
      escalateToTeamId: rule.escalateToTeamId,
      priority: rule.priority || '',
      isActive: rule.isActive,
    });
    setShowEscalationModal(true);
  };

  const closeEscalationModal = () => {
    setShowEscalationModal(false);
    setEditingRule(null);
    setEscalationForm(initialEscalationForm);
  };

  const openDeleteConfirm = (type: TabType, id: string) => {
    setDeletingItem({ type, id });
    setShowDeleteConfirm(true);
  };

  const closeDeleteConfirm = () => {
    setShowDeleteConfirm(false);
    setDeletingItem(null);
  };

  const handleDelete = () => {
    if (!deletingItem) return;
    switch (deletingItem.type) {
      case 'policies':
        deletePolicyMutation.mutate(deletingItem.id);
        break;
      case 'teams':
        deleteTeamMutation.mutate(deletingItem.id);
        break;
      case 'escalation':
        deleteEscalationMutation.mutate(deletingItem.id);
        break;
    }
  };

  const handlePolicySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingPolicy) {
      updatePolicyMutation.mutate({ id: editingPolicy.id, data: policyForm });
    } else {
      createPolicyMutation.mutate(policyForm);
    }
  };

  const handleTeamSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingTeam) {
      updateTeamMutation.mutate({ id: editingTeam.id, data: teamForm });
    } else {
      createTeamMutation.mutate(teamForm);
    }
  };

  const handleEscalationSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingRule) {
      updateEscalationMutation.mutate({ id: editingRule.id, data: escalationForm });
    } else {
      createEscalationMutation.mutate(escalationForm);
    }
  };

  const policies: SLAPolicy[] = policiesData?.data || [];
  const teams: SupportTeam[] = teamsData?.data || [];
  const escalationRules: EscalationRule[] = escalationData?.data || [];
  const allTeams: SupportTeam[] = allTeamsData?.data || [];

  const getPagination = () => {
    switch (activeTab) {
      case 'policies': return policiesData?.pagination;
      case 'teams': return teamsData?.pagination;
      case 'escalation': return escalationData?.pagination;
    }
  };

  const getIsLoading = () => {
    switch (activeTab) {
      case 'policies': return policiesLoading;
      case 'teams': return teamsLoading;
      case 'escalation': return escalationLoading;
    }
  };

  const pagination = getPagination();
  const isLoading = getIsLoading();

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    setSearch('');
    setPage(1);
  };

  const getPriorityBadge = (priority: string) => {
    const colors: Record<string, string> = {
      LOW: 'bg-gray-100 text-gray-800',
      MEDIUM: 'bg-blue-100 text-blue-800',
      HIGH: 'bg-orange-100 text-orange-800',
      URGENT: 'bg-red-100 text-red-800',
      CRITICAL: 'bg-purple-100 text-purple-800',
    };
    return colors[priority] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">SLA Management</h1>
          <p className="text-muted-foreground">Configure service level agreements and escalations</p>
        </div>
        <Button onClick={() => {
          if (activeTab === 'policies') openCreatePolicyModal();
          else if (activeTab === 'teams') openCreateTeamModal();
          else openCreateEscalationModal();
        }}>
          <Plus className="w-4 h-4 mr-2" />
          Add {activeTab === 'policies' ? 'Policy' : activeTab === 'teams' ? 'Team' : 'Rule'}
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex border-b">
        <button
          className={cn(
            "flex items-center gap-2 px-4 py-2 border-b-2 font-medium text-sm transition-colors",
            activeTab === 'policies' 
              ? "border-primary text-primary" 
              : "border-transparent text-muted-foreground hover:text-foreground"
          )}
          onClick={() => handleTabChange('policies')}
        >
          <Shield className="w-4 h-4" />
          Policies
        </button>
        <button
          className={cn(
            "flex items-center gap-2 px-4 py-2 border-b-2 font-medium text-sm transition-colors",
            activeTab === 'teams' 
              ? "border-primary text-primary" 
              : "border-transparent text-muted-foreground hover:text-foreground"
          )}
          onClick={() => handleTabChange('teams')}
        >
          <Users className="w-4 h-4" />
          Support Teams
        </button>
        <button
          className={cn(
            "flex items-center gap-2 px-4 py-2 border-b-2 font-medium text-sm transition-colors",
            activeTab === 'escalation' 
              ? "border-primary text-primary" 
              : "border-transparent text-muted-foreground hover:text-foreground"
          )}
          onClick={() => handleTabChange('escalation')}
        >
          <ArrowUpCircle className="w-4 h-4" />
          Escalation Rules
        </button>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder={`Search ${activeTab}...`}
              className="pl-10"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
          </div>
        </CardContent>
      </Card>

      {/* Content */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <>
              {/* Policies Table */}
              {activeTab === 'policies' && (
                policies.length === 0 ? (
                  <div className="text-center py-12">
                    <Shield className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">No SLA policies found</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="text-left p-4 font-medium">Name</th>
                          <th className="text-left p-4 font-medium">Priority</th>
                          <th className="text-left p-4 font-medium">Response Time</th>
                          <th className="text-left p-4 font-medium">Resolution Time</th>
                          <th className="text-left p-4 font-medium">Status</th>
                          <th className="text-right p-4 font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {policies.map((policy) => (
                          <tr key={policy.id} className="hover:bg-muted/50">
                            <td className="p-4">
                              <div>
                                <p className="font-medium">{policy.name}</p>
                                {policy.description && (
                                  <p className="text-sm text-muted-foreground">{policy.description}</p>
                                )}
                              </div>
                            </td>
                            <td className="p-4">
                              <span className={cn("px-2 py-1 text-xs font-medium rounded-full", getPriorityBadge(policy.priority))}>
                                {policy.priority}
                              </span>
                            </td>
                            <td className="p-4">
                              <div className="flex items-center gap-1">
                                <Clock className="w-4 h-4 text-muted-foreground" />
                                {policy.firstResponseMinutes >= 60 
                                  ? `${Math.floor(policy.firstResponseMinutes / 60)}h${policy.firstResponseMinutes % 60 > 0 ? ` ${policy.firstResponseMinutes % 60}m` : ''}`
                                  : `${policy.firstResponseMinutes}m`}
                              </div>
                            </td>
                            <td className="p-4">
                              <div className="flex items-center gap-1">
                                <AlertTriangle className="w-4 h-4 text-muted-foreground" />
                                {policy.resolutionMinutes >= 60 
                                  ? `${Math.floor(policy.resolutionMinutes / 60)}h${policy.resolutionMinutes % 60 > 0 ? ` ${policy.resolutionMinutes % 60}m` : ''}`
                                  : `${policy.resolutionMinutes}m`}
                              </div>
                            </td>
                            <td className="p-4">
                              <span className={cn(
                                "px-2 py-1 text-xs font-medium rounded-full",
                                policy.isActive ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"
                              )}>
                                {policy.isActive ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td className="p-4">
                              <div className="flex items-center justify-end gap-1">
                                <Button variant="ghost" size="icon" onClick={() => openEditPolicyModal(policy)}>
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="icon" onClick={() => openDeleteConfirm('policies', policy.id)}>
                                  <Trash2 className="w-4 h-4 text-destructive" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
              )}

              {/* Teams Table */}
              {activeTab === 'teams' && (
                teams.length === 0 ? (
                  <div className="text-center py-12">
                    <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">No support teams found</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="text-left p-4 font-medium">Name</th>
                          <th className="text-left p-4 font-medium">Email</th>
                          <th className="text-left p-4 font-medium">Members</th>
                          <th className="text-left p-4 font-medium">Status</th>
                          <th className="text-right p-4 font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {teams.map((team) => (
                          <tr key={team.id} className="hover:bg-muted/50">
                            <td className="p-4">
                              <div>
                                <p className="font-medium">{team.name}</p>
                                {team.description && (
                                  <p className="text-sm text-muted-foreground">{team.description}</p>
                                )}
                              </div>
                            </td>
                            <td className="p-4 text-muted-foreground">{team.email || '-'}</td>
                            <td className="p-4">
                              <div className="flex flex-wrap gap-1">
                                {team.members?.length > 0 ? (
                                  team.members.slice(0, 3).map((member) => (
                                    <span key={member.id} className="px-2 py-0.5 text-xs bg-muted rounded">
                                      {member.user.firstName} {member.user.lastName}
                                      {member.role === 'LEAD' && ' (Lead)'}
                                    </span>
                                  ))
                                ) : (
                                  <span className="text-muted-foreground text-sm">No members</span>
                                )}
                                {team.members?.length > 3 && (
                                  <span className="px-2 py-0.5 text-xs bg-muted rounded">
                                    +{team.members.length - 3} more
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="p-4">
                              <span className={cn(
                                "px-2 py-1 text-xs font-medium rounded-full",
                                team.isActive ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"
                              )}>
                                {team.isActive ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td className="p-4">
                              <div className="flex items-center justify-end gap-1">
                                <Button variant="ghost" size="icon" onClick={() => openEditTeamModal(team)}>
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="icon" onClick={() => openDeleteConfirm('teams', team.id)}>
                                  <Trash2 className="w-4 h-4 text-destructive" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
              )}

              {/* Escalation Rules Table */}
              {activeTab === 'escalation' && (
                escalationRules.length === 0 ? (
                  <div className="text-center py-12">
                    <ArrowUpCircle className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">No escalation rules found</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="text-left p-4 font-medium">Name</th>
                          <th className="text-left p-4 font-medium">Trigger After</th>
                          <th className="text-left p-4 font-medium">Priority</th>
                          <th className="text-left p-4 font-medium">Escalate To</th>
                          <th className="text-left p-4 font-medium">Status</th>
                          <th className="text-right p-4 font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {escalationRules.map((rule) => (
                          <tr key={rule.id} className="hover:bg-muted/50">
                            <td className="p-4">
                              <div>
                                <p className="font-medium">{rule.name}</p>
                                {rule.description && (
                                  <p className="text-sm text-muted-foreground">{rule.description}</p>
                                )}
                              </div>
                            </td>
                            <td className="p-4">
                              <span className="font-mono">{rule.triggerAfterHours}h</span>
                            </td>
                            <td className="p-4">
                              {rule.priority ? (
                                <span className={cn("px-2 py-1 text-xs font-medium rounded-full", getPriorityBadge(rule.priority))}>
                                  {rule.priority}
                                </span>
                              ) : (
                                <span className="text-muted-foreground">All</span>
                              )}
                            </td>
                            <td className="p-4">
                              {rule.escalateToTeam?.name || '-'}
                            </td>
                            <td className="p-4">
                              <span className={cn(
                                "px-2 py-1 text-xs font-medium rounded-full",
                                rule.isActive ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"
                              )}>
                                {rule.isActive ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td className="p-4">
                              <div className="flex items-center justify-end gap-1">
                                <Button variant="ghost" size="icon" onClick={() => openEditEscalationModal(rule)}>
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="icon" onClick={() => openDeleteConfirm('escalation', rule.id)}>
                                  <Trash2 className="w-4 h-4 text-destructive" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
              )}
            </>
          )}
        </CardContent>

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className="flex items-center justify-between p-4 border-t">
            <p className="text-sm text-muted-foreground">
              Showing {((page - 1) * pagination.limit) + 1} to {Math.min(page * pagination.limit, pagination.total)} of {pagination.total}
            </p>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" disabled={page === 1} onClick={() => setPage(page - 1)}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm">Page {page} of {pagination.totalPages}</span>
              <Button variant="outline" size="icon" disabled={page === pagination.totalPages} onClick={() => setPage(page + 1)}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Policy Modal */}
      {showPolicyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-md mx-4">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold">
                  {editingPolicy ? 'Edit SLA Policy' : 'Create SLA Policy'}
                </h2>
                <Button variant="ghost" size="icon" onClick={closePolicyModal}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <form onSubmit={handlePolicySubmit} className="space-y-4">
                <div>
                  <Label htmlFor="policy-name">Name *</Label>
                  <Input
                    id="policy-name"
                    value={policyForm.name}
                    onChange={(e) => setPolicyForm({ ...policyForm, name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="policy-desc">Description</Label>
                  <Input
                    id="policy-desc"
                    value={policyForm.description}
                    onChange={(e) => setPolicyForm({ ...policyForm, description: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="policy-priority">Priority *</Label>
                  <select
                    id="policy-priority"
                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                    value={policyForm.priority}
                    onChange={(e) => setPolicyForm({ ...policyForm, priority: e.target.value as any })}
                    required
                  >
                    <option value="LOW">Low</option>
                    <option value="MEDIUM">Medium</option>
                    <option value="HIGH">High</option>
                    <option value="URGENT">Urgent</option>
                    <option value="CRITICAL">Critical</option>
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="policy-response">First Response (minutes) *</Label>
                    <Input
                      id="policy-response"
                      type="number"
                      min="1"
                      value={policyForm.firstResponseMinutes}
                      onChange={(e) => setPolicyForm({ ...policyForm, firstResponseMinutes: parseInt(e.target.value) || 1 })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="policy-resolution">Resolution Time (minutes) *</Label>
                    <Input
                      id="policy-resolution"
                      type="number"
                      min="1"
                      value={policyForm.resolutionMinutes}
                      onChange={(e) => setPolicyForm({ ...policyForm, resolutionMinutes: parseInt(e.target.value) || 1 })}
                      required
                    />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="policy-businesshours"
                    checked={policyForm.businessHoursOnly}
                    onChange={(e) => setPolicyForm({ ...policyForm, businessHoursOnly: e.target.checked })}
                    className="w-4 h-4 rounded border-input"
                  />
                  <Label htmlFor="policy-businesshours">Business Hours Only</Label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="policy-active"
                    checked={policyForm.isActive}
                    onChange={(e) => setPolicyForm({ ...policyForm, isActive: e.target.checked })}
                    className="w-4 h-4 rounded border-input"
                  />
                  <Label htmlFor="policy-active">Active</Label>
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={closePolicyModal}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={createPolicyMutation.isPending || updatePolicyMutation.isPending}>
                    {editingPolicy ? 'Update' : 'Create'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Team Modal */}
      {showTeamModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-md mx-4">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold">
                  {editingTeam ? 'Edit Support Team' : 'Create Support Team'}
                </h2>
                <Button variant="ghost" size="icon" onClick={closeTeamModal}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <form onSubmit={handleTeamSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="team-name">Name *</Label>
                  <Input
                    id="team-name"
                    value={teamForm.name}
                    onChange={(e) => setTeamForm({ ...teamForm, name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="team-desc">Description</Label>
                  <Input
                    id="team-desc"
                    value={teamForm.description}
                    onChange={(e) => setTeamForm({ ...teamForm, description: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="team-email">Email</Label>
                  <Input
                    id="team-email"
                    type="email"
                    value={teamForm.email}
                    onChange={(e) => setTeamForm({ ...teamForm, email: e.target.value })}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="team-active"
                    checked={teamForm.isActive}
                    onChange={(e) => setTeamForm({ ...teamForm, isActive: e.target.checked })}
                    className="w-4 h-4 rounded border-input"
                  />
                  <Label htmlFor="team-active">Active</Label>
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={closeTeamModal}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={createTeamMutation.isPending || updateTeamMutation.isPending}>
                    {editingTeam ? 'Update' : 'Create'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Escalation Modal */}
      {showEscalationModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-md mx-4">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold">
                  {editingRule ? 'Edit Escalation Rule' : 'Create Escalation Rule'}
                </h2>
                <Button variant="ghost" size="icon" onClick={closeEscalationModal}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <form onSubmit={handleEscalationSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="esc-name">Name *</Label>
                  <Input
                    id="esc-name"
                    value={escalationForm.name}
                    onChange={(e) => setEscalationForm({ ...escalationForm, name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="esc-desc">Description</Label>
                  <Input
                    id="esc-desc"
                    value={escalationForm.description}
                    onChange={(e) => setEscalationForm({ ...escalationForm, description: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="esc-trigger">Trigger After (hours) *</Label>
                  <Input
                    id="esc-trigger"
                    type="number"
                    min="1"
                    value={escalationForm.triggerAfterHours}
                    onChange={(e) => setEscalationForm({ ...escalationForm, triggerAfterHours: parseInt(e.target.value) || 1 })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="esc-priority">Priority (optional)</Label>
                  <select
                    id="esc-priority"
                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                    value={escalationForm.priority}
                    onChange={(e) => setEscalationForm({ ...escalationForm, priority: e.target.value })}
                  >
                    <option value="">All Priorities</option>
                    <option value="LOW">Low</option>
                    <option value="MEDIUM">Medium</option>
                    <option value="HIGH">High</option>
                    <option value="URGENT">Urgent</option>
                    <option value="CRITICAL">Critical</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="esc-team">Escalate To Team *</Label>
                  <select
                    id="esc-team"
                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                    value={escalationForm.escalateToTeamId}
                    onChange={(e) => setEscalationForm({ ...escalationForm, escalateToTeamId: e.target.value })}
                    required
                  >
                    <option value="">Select a team</option>
                    {allTeams.map((team) => (
                      <option key={team.id} value={team.id}>{team.name}</option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="esc-active"
                    checked={escalationForm.isActive}
                    onChange={(e) => setEscalationForm({ ...escalationForm, isActive: e.target.checked })}
                    className="w-4 h-4 rounded border-input"
                  />
                  <Label htmlFor="esc-active">Active</Label>
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={closeEscalationModal}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={createEscalationMutation.isPending || updateEscalationMutation.isPending}>
                    {editingRule ? 'Update' : 'Create'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-md mx-4">
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold mb-2">Confirm Deletion</h3>
              <p className="text-muted-foreground mb-6">
                Are you sure you want to delete this {deletingItem?.type === 'policies' ? 'policy' : deletingItem?.type === 'teams' ? 'team' : 'rule'}? This action cannot be undone.
              </p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={closeDeleteConfirm}>
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDelete}
                  disabled={deletePolicyMutation.isPending || deleteTeamMutation.isPending || deleteEscalationMutation.isPending}
                >
                  Delete
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
